import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, IconButton, TextField, Box, Dialog, DialogTitle, DialogContent, CircularProgress, Chip } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Edit as EditIcon, PlayArrow as PlayArrowIcon, Delete as DeleteIcon, Close as CloseIcon } from '@mui/icons-material';
import ReactPlayer from 'react-player';
import { useParams, useNavigate } from 'react-router-dom'; // Import useParams and useNavigate
import { db } from './firebase.ts'; // Import db
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore'; // Import firestore functions
import './TaskDetailView.css';
import { useTheme } from './ThemeContext.tsx'; // Import useTheme

import { User } from 'firebase/auth';

interface TaskData {
  id: string;
  name: string;
  description: string;
  videoUrl?: string;
  completed: boolean;
  recurrence?: { type: string };
  totalTasks?: number; // Add totalTasks to TaskData interface
  completedTasks?: number; // Add completedTasks to TaskData interface
}

interface TaskDetailViewProps {
  user: User | null;
}

function TaskDetailView({ user }: TaskDetailViewProps) { // Accept user prop
  const { checklistId, taskId } = useParams<{ checklistId: string; taskId: string }>(); // Get checklistId and taskId from URL
  const navigate = useNavigate(); // Initialize useNavigate
  const [task, setTask] = useState<TaskData | null>(null);
  const [checklistName, setChecklistName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingDescription, setIsEditingDescription] = useState<boolean>(false);
  const [editedDescription, setEditedDescription] = useState<string>('');
  const [showVideoPlayer, setShowVideoPlayer] = useState<boolean>(false);
  const { theme } = useTheme(); // Call useTheme here, once at the top level of the component

  useEffect(() => {
    const fetchTask = async () => {
      if (!user || !checklistId || !taskId) {
        setLoading(false);
        return;
      }

      try {
        const checklistRef = doc(db, `users/${user.uid}/checklists`, checklistId);
        const checklistSnap = await getDoc(checklistRef);
        if (checklistSnap.exists()) {
          setChecklistName(checklistSnap.data().name);
        } else {
          console.warn("Checklist not found!");
        }

        const taskRef = doc(db, `users/${user.uid}/checklists/${checklistId}/tasks`, taskId);
        const taskSnap = await getDoc(taskRef);

        if (taskSnap.exists()) {
          const taskData: TaskData = {
            id: taskSnap.id,
            name: taskSnap.data().name,
            description: taskSnap.data().description,
            videoUrl: taskSnap.data().videoUrl,
            completed: taskSnap.data().completed,
            recurrence: taskSnap.data().recurrence || { type: 'none' }, // Ensure recurrence is an object, default to none
          };
          setTask(taskData);
          setEditedDescription(taskData.description || '');
        } else {
          setError("Task not found.");
        }
      } catch (err) {
        console.error("Error fetching task:", err);
        setError("Failed to load task details.");
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [user, checklistId, taskId]);

  const handleBack = () => {
    navigate(`/checklists`); // Navigate back to the checklists page
  };

  const handleDeleteTask = async () => {
    if (!user || !checklistId || !taskId) return;
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        const taskRef = doc(db, `users/${user.uid}/checklists/${checklistId}/tasks`, taskId);
        const taskSnap = await getDoc(taskRef);
        
        if (taskSnap.exists()) {
          const taskData = taskSnap.data();
          const wasCompleted = taskData.completed;

          await deleteDoc(taskRef); // Delete the task

          // Update parent checklist's totalTasks and completedTasks counts
          const checklistRef = doc(db, `users/${user.uid}/checklists`, checklistId);
          const checklistDoc = await getDoc(checklistRef);
          if (checklistDoc.exists()) {
            const currentTotalTasks = checklistDoc.data().totalTasks || 0;
            const currentCompletedTasks = checklistDoc.data().completedTasks || 0;

            await updateDoc(checklistRef, {
              totalTasks: currentTotalTasks - 1, // Decrement totalTasks
              completedTasks: wasCompleted ? currentCompletedTasks - 1 : currentCompletedTasks, // Decrement completedTasks if it was completed
            });
          }

          alert("Task deleted successfully!");
          navigate(`/checklists`); // Go back to checklists after deletion
        } else {
          alert("Task not found.");
        }
      } catch (error) {
        console.error("Error deleting task:", error);
        alert("Failed to delete task.");
      }
    }
  };

  const handleEditDescription = async () => {
    if (!user || !checklistId || !taskId) return;
    try {
      const taskRef = doc(db, `users/${user.uid}/checklists/${checklistId}/tasks`, taskId);
      await updateDoc(taskRef, {
        description: editedDescription,
      });
      setTask(prevTask => (prevTask ? { ...prevTask, description: editedDescription } : null));
      setIsEditingDescription(false);
      alert("Description updated successfully!");
    } catch (error) {
      console.error("Error updating description:", error);
      alert("Failed to update description.");
    }
  };

  const handleEditClick = () => {
    setIsEditingDescription(true);
  };

  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false);
  };

  const handleSaveDescription = () => {
    handleEditDescription(); // Call the async function
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ textAlign: 'center', mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!task) {
    return (
      <Container sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6">Task not found.</Typography>
        <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
          Go Back to Checklists
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" className="task-detail-container">
      <Box className="task-detail-header">
        <IconButton onClick={handleBack} className="back-button">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" className="header-title">
          {checklistName}
        </Typography>
      </Box>

      <Box className="task-title-container">
        <Typography variant="h4" className="task-title">
          {task.name}
        </Typography>
        <Box className="edit-save-button-container">
          {!isEditingDescription ? (
            <IconButton onClick={handleEditClick} className="edit-icon-button">
              <EditIcon />
            </IconButton>
          ) : (
            <Button onClick={handleSaveDescription} variant="text" size="small" sx={{ textTransform: 'none', fontWeight: 'bold', color: theme.colors.primaryRed }}>Save</Button>
          )}
        </Box>
      </Box>

      {task.recurrence?.type && typeof task.recurrence.type === 'string' && task.recurrence.type !== 'none' && (
        <Chip
          label={task.recurrence.type.charAt(0).toUpperCase() + task.recurrence.type.slice(1)}
          size="small"
          sx={{
            mt: 1, // Margin top to place it under the title container
            mb: 2, // Margin bottom for spacing before next section
            backgroundColor: theme.colors.primaryRed,
            color: theme.colors.whiteCardBackground,
            fontWeight: 'bold',
            fontSize: '0.8em',
            height: '24px',
          }}
        />
      )}

      <Box className="detailed-steps-section">
        <Box className="section-header">
          <Typography variant="subtitle1" className="section-title">
            Detailed Steps & Notes
          </Typography>
        </Box>
        {isEditingDescription ? (
          <TextField
            multiline
            fullWidth
            rows={4}
            value={editedDescription}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditedDescription(e.target.value)}
            variant="outlined"
            className="description-textarea"
            InputLabelProps={{
              sx: { color: theme.colors.lightText }
            }}
            InputProps={{
              sx: { color: theme.colors.darkText }
            }}
          />
        ) : (
          <Box className="description-display">
            <Typography variant="body1" sx={{ color: theme.colors.darkText }}>{task.description || 'No detailed steps or notes.'}</Typography>
          </Box>
        )}
      </Box>

      <Typography variant="body2" className="part-of-text">
        Part of: {checklistName} checklist.
      </Typography>

      {task.videoUrl && (
        <Button
          variant="contained"
          className="play-video-button"
          onClick={() => setShowVideoPlayer(true)}
          startIcon={<PlayArrowIcon />}
        >
          Play Video
        </Button>
      )}

      {showVideoPlayer && task.videoUrl && (
        <Dialog
          open={showVideoPlayer}
          onClose={handleCloseVideoPlayer}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: theme.colors.whiteCardBackground,
              color: theme.colors.darkText,
              borderRadius: theme.borderRadius,
              boxShadow: theme.boxShadow,
              border: `1px solid ${theme.colors.borderColor}`,
            }
          }}
        >
          <DialogTitle sx={{ color: theme.colors.darkText, fontWeight: 'bold' }}>
            Instructional Video
            <IconButton
              aria-label="close"
              onClick={handleCloseVideoPlayer}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme.colors.lightText,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.colors.whiteCardBackground }}>
            <Box className="video-player-container">
              <ReactPlayer url={task.videoUrl} width="100%" height="auto" controls />
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {isEditingDescription && ( /* Render delete button only when editing */
        <Button
          variant="outlined"
          color="error"
          className="delete-task-button"
          onClick={handleDeleteTask}
          startIcon={<DeleteIcon />}
        >
          Delete Task
        </Button>
      )}
    </Container>
  );
}

export default TaskDetailView;
